'use client';

import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import CountrySelector from '@/components/CountrySelector';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function Header() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const nav = t.nav;

  const spokes = [
    { id: 'book',  label: nav.book  },
    { id: 'track', label: nav.track },
    { id: 'pay',   label: nav.pay   },
    { id: 'rate',  label: nav.rate  },
  ];

  const PLATFORM_LINKS = [
    { href: '/marketplace', label: nav.marketplace, icon: '🏪' },
    { href: '/professional-services', label: nav.services, icon: '👷' },
    { href: '/delivery', label: nav.delivery, icon: '🚚' },
    { href: '/equipment-rental', label: nav.rental, icon: '🏗️' },
    { href: '/insurance-financing', label: nav.insurance, icon: '🛡️' },
    { href: '/project-management', label: nav.projects, icon: '📊' },
    { href: '/dashboard', label: nav.dashboard || 'Mon tableau de bord', icon: '👤' },
    { href: '/professional-dashboard', label: nav.professionalDashboard || 'Tableau Pro', icon: '🔧' },
    { href: '/booking-confirmation', label: nav.bookingConfirmation || 'Confirmation', icon: '✅' },
    { href: '/professional/register', label: nav.joinPro, icon: '🪪' },
    { href: '/admin/professionals', label: nav.adminPanel, icon: '⚙️' },
  ];

  const [scrolled,  setScrolled]  = useState(false);
  const [activeSpoke, setActiveSpoke] = useState('');
  const [heroResolved, setHeroResolved] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      for (const spoke of [...spokes].reverse()) {
        const el = document.getElementById(spoke.id);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSpoke(spoke.id);
          return;
        }
      }
      setActiveSpoke('');
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    const onHeroResolved = () => setHeroResolved(true);
    window.addEventListener('hero-resolved', onHeroResolved);

    const timer = setTimeout(() => setHeroResolved(true), 5000);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('hero-resolved', onHeroResolved);
      clearTimeout(timer);
    };
  }, [language]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'py-3 bg-dispatch-bg/95 backdrop-blur-xl border-b border-dispatch-surface' :'py-5 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <AppLogo
            size={28}
            text="Dispatch"
            iconName="BoltIcon"
            className="text-dispatch-text font-manrope font-bold text-lg tracking-tight"
          />
        </div>

        {/* Anchor spokes */}
        <div className="hidden lg:flex items-center gap-1 bg-dispatch-surface/50 backdrop-blur-md border border-dispatch-surface px-2 py-1.5 rounded-full">
          {spokes.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`nav-spoke px-3 py-1.5 rounded-full text-xs font-semibold font-mono tracking-widest uppercase transition-all duration-300 border-b-2 border-transparent ${
                activeSpoke === s.id
                  ? 'active bg-dispatch-cyan/10 text-dispatch-cyan border-dispatch-cyan' :'text-dispatch-muted hover:text-dispatch-text'
              }`}
            >
              {s.label}
            </button>
          ))}

          {/* Platform dropdown trigger */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="nav-spoke px-3 py-1.5 rounded-full text-xs font-semibold font-mono tracking-widest uppercase transition-all duration-300 border-b-2 border-transparent text-dispatch-muted hover:text-dispatch-text flex items-center gap-1"
            >
              {nav.platform}
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className={`transition-transform duration-300 ${menuOpen ? 'rotate-180' : ''}`}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {menuOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 rounded-2xl border border-dispatch-surface overflow-hidden shadow-2xl"
                style={{ background: 'rgba(10,22,40,0.97)', backdropFilter: 'blur(20px)' }}
              >
                <div className="p-2">
                  {PLATFORM_LINKS.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-dispatch-muted hover:text-dispatch-text hover:bg-dispatch-surface/60 transition-all duration-200 group"
                    >
                      <span className="text-base">{link.icon}</span>
                      <span className="font-mono text-xs font-semibold tracking-wide">{link.label}</span>
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div
          className={`flex items-center gap-3 shrink-0 transition-all duration-700 ${
            heroResolved ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
          }`}
        >
          <CountrySelector />
          <a
            href="#download"
            className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-full bg-dispatch-cyan text-dispatch-bg text-xs font-bold font-mono tracking-wide hover:bg-white transition-colors duration-300 shadow-cyan-sm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.42.07 2.38.74 3.2.8 1.22-.24 2.39-.93 3.72-.84 1.58.13 2.77.77 3.54 1.96-3.27 1.96-2.49 6.27.54 7.48-.65 1.72-1.49 3.43-3 4.46zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            {nav.downloadApp}
          </a>
        </div>
      </div>

      {menuOpen && (
        <div
          className="fixed inset-0 z-[-1]"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </nav>
  );
}